import { useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchAdminStatus } from './adminApi';

export interface AdminStatusResult {
  /** The probe has not answered yet. Render nothing decisive until it has. */
  isChecking: boolean;
  isAdmin: boolean;
  /** The operator's own email, as the backend sees it. Empty when not admin. */
  email: string;
}

interface Probe {
  isAdmin: boolean;
  email: string;
}

/**
 * One probe per signed-in user per page session, keyed by user id.
 *
 * Keyed rather than global because a sign-out does not reload the SPA: without
 * the key, the next person to sign in on the same tab would inherit the previous
 * one's answer.
 */
const probes = new Map<string, Promise<Probe>>();

const DENIED: Probe = { isAdmin: false, email: '' };

function probeFor(userId: string): Promise<Probe> {
  const cached = probes.get(userId);
  if (cached) return cached;

  const probe = fetchAdminStatus()
    .then((status) => ({ isAdmin: true, email: status.email }))
    // Fail closed, and identically for every failure. A 404 is the backend
    // saying "no such surface" to a non-operator; anything else (offline, 500)
    // is not evidence of a rank, so it is not treated as one.
    .catch(() => DENIED);

  probes.set(userId, probe);
  return probe;
}

/**
 * Whether the signed-in user is an Autopostmate operator.
 *
 * The answer is the backend's alone — there is no admin flag on the profile, no
 * claim in the token and nothing in local storage to tamper with. This hook only
 * decides whether to render the panel or a 404; every route behind it is gated
 * again server-side, so a client that lied to itself would see an empty shell
 * and a wall of 404s.
 */
export function useAdminStatus(): AdminStatusResult {
  const { user, isLoading } = useAuth();
  const userId = user?.id ?? null;
  const [result, setResult] = useState<{ userId: string; probe: Probe } | null>(null);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    void probeFor(userId).then((probe) => {
      if (cancelled) return;
      setResult({ userId, probe });
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const isCurrent = result !== null && result.userId === userId;

  return {
    isChecking: isLoading || (userId !== null && !isCurrent),
    isAdmin: isCurrent && result.probe.isAdmin,
    email: isCurrent ? result.probe.email : '',
  };
}
